import React, { useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "react-query";
import { request } from "../../config/helpers/axios-instance";
import Loader from "../Loader";

const RANGE_OPTIONS = [{ label: "24 hours", days: 1 }, { label: "7 days", days: 7 }, { label: "30 days", days: 30 }];

const percent = (ratio) => (ratio == null ? "—" : `${Math.round(ratio * 100)}%`);

function Stat({ label, value }) {
    return (
        <div className="bg-gray-50 rounded-xl px-4 py-3 flex flex-col gap-1 min-w-[120px]">
            <span className="text-xs text-gray-400 font-sans font-medium">{label}</span>
            <span className="text-xl font-bold font-sans text-gray-800">{value}</span>
        </div>
    );
}

Stat.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

function CountBars({ rows, labelOf, countOf, tone }) {
    const max = Math.max(1, ...rows.map(countOf));
    return (
        <div className="flex flex-col gap-2">
            {rows.map((row) => (
                <div key={labelOf(row)} className="flex items-center gap-3">
                    <span className="w-56 truncate text-xs text-gray-600 font-sans">{labelOf(row)}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                        <div className={`h-3 rounded-full ${tone}`} style={{ width: `${Math.max(2, (countOf(row) / max) * 100)}%` }} />
                    </div>
                    <span className="w-12 text-right text-xs font-semibold text-gray-700 font-sans">{countOf(row)}</span>
                </div>
            ))}
        </div>
    );
}

CountBars.propTypes = {
    rows:    PropTypes.arrayOf(PropTypes.object).isRequired,
    labelOf: PropTypes.func.isRequired,
    countOf: PropTypes.func.isRequired,
    tone:    PropTypes.string.isRequired,
};

export default function FunnelSummary() {
    const [days, setDays] = useState(7);
    const { data, isFetching, isError } = useQuery(
        ["pricing-funnel-summary", days],
        () => request({
            url: `ops/pricing/funnel?from=${encodeURIComponent(new Date(Date.now() - days * 86400000).toISOString())}`,
            method: "GET",
        }),
        { refetchOnWindowFocus: false }
    );
    const summary = data?.data?.data;

    return (
        <div className="bg-white rounded-2xl shadow-soft-xl p-5 flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold font-sans text-gray-800">Pricing funnel — all carts</h2>
                    <p className="text-xs font-sans text-gray-400">Where lines drop out, and how each source performs.</p>
                </div>
                <div className="flex gap-2">
                    {RANGE_OPTIONS.map((option) => (
                        <button
                            key={option.days}
                            onClick={() => setDays(option.days)}
                            className={`px-3 py-1 rounded-lg text-xs font-semibold font-sans ${days === option.days ? "bg-[#069079] text-white" : "bg-gray-100 text-gray-600"}`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            {isFetching && <div className="flex justify-center py-8"><Loader size="large" text="Loading funnel…" /></div>}
            {!isFetching && isError && <p className="text-sm text-gray-400 font-sans text-center py-6">Could not load the funnel summary.</p>}
            {!isFetching && summary && summary.lines === 0 && (
                <p className="text-sm text-gray-400 font-sans text-center py-6">No funnel data in this period.</p>
            )}

            {!isFetching && summary && summary.lines > 0 && (
                <>
                    <div className="flex gap-3 flex-wrap">
                        <Stat label="Lines priced" value={summary.lines} />
                        <Stat label="READY" value={`${summary.readyLines} (${percent(summary.readyLines / summary.lines)})`} />
                        <Stat label="Failed / review" value={summary.lines - summary.readyLines} />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div>
                            <p className="text-xs uppercase tracking-wide text-gray-400 font-sans font-semibold mb-2">Failures by stage and reason</p>
                            {summary.failuresByStage.length === 0
                                ? <p className="text-xs text-gray-400 font-sans italic">No failures.</p>
                                : <CountBars rows={summary.failuresByStage} labelOf={(row) => `${row.stage} · ${row.reason}`} countOf={(row) => row.count} tone="bg-red-400" />}
                        </div>
                        <div>
                            <p className="text-xs uppercase tracking-wide text-gray-400 font-sans font-semibold mb-2">Candidates rejected at the gate</p>
                            {summary.gateRejections.length === 0
                                ? <p className="text-xs text-gray-400 font-sans italic">No rejections.</p>
                                : <CountBars rows={summary.gateRejections} labelOf={(row) => row.reason} countOf={(row) => row.count} tone="bg-yellow-400" />}
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-xs font-sans">
                            <thead>
                                <tr className="text-left text-gray-400 text-[10px] uppercase tracking-wide border-b border-gray-100">
                                    <th className="py-2 pl-2">Source</th>
                                    <th>Asked</th>
                                    <th>Error rate</th>
                                    <th>Candidates</th>
                                    <th>Gate pass rate</th>
                                    <th>Wins</th>
                                    <th>Avg latency</th>
                                </tr>
                            </thead>
                            <tbody>
                                {summary.sources.map((source) => (
                                    <tr key={source.source} className="border-b border-gray-50">
                                        <td className="py-2 pl-2 font-semibold text-gray-800">{source.source}</td>
                                        <td>{source.asked}</td>
                                        <td className={source.errorRate > 0.2 ? "text-red-600 font-semibold" : ""}>{percent(source.errorRate)}</td>
                                        <td>{source.candidatesReturned}</td>
                                        <td>{percent(source.gatePassRate)}</td>
                                        <td>{source.wins}</td>
                                        <td>{source.avgLatencyMs != null ? `${source.avgLatencyMs}ms` : "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}
